import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import type { QuotaExceededError } from '@datarecs/data-models-core-api';
import { ToastService } from './toast.service';
import {
    QuotaUpgradeDialogComponent,
    QuotaUpgradeDialogData
} from '../../shared/components/quota-upgrade-dialog/quota-upgrade-dialog.component';
import { metaFor, formatValue, isTransientEntitlement } from '../../shared/quota/entitlement-meta';

/**
 * Renders the two quota-related server responses into the right UX, centrally:
 *
 * - `QUOTA_EXCEEDED` (HTTP 403) → an upgrade-prompt modal naming the entitlement and the
 *   current/limit, with a clear "View usage / Upgrade" next step (not a generic error toast).
 *   The exception is a *transient* entitlement (e.g. `max_concurrent_runs`) — a momentary ceiling
 *   the user clears simply by waiting for in-flight work to finish — which gets a calm "wait, then
 *   try again" toast instead of an upgrade prompt (upgrading wouldn't be the right next step).
 * - `RATE_LIMITED` (HTTP 429) → a calm, non-alarming "you're going too fast" toast with the
 *   server's retry hint.
 *
 * Both entry points are driven by the quota-error HTTP interceptor, so every create flow in the
 * app benefits without touching its own error handler. The service owns de-duplication (a burst
 * of rejected creates shows one prompt, not a stack) and unit-aware formatting.
 */
@Injectable({ providedIn: 'root' })
export class QuotaPromptService {
    private dialog = inject(MatDialog);
    private toast = inject(ToastService);

    /** The currently-open upgrade dialog, if any — so concurrent 403s don't stack modals. */
    private openDialog: MatDialogRef<QuotaUpgradeDialogComponent> | null = null;

    /** Throttle the slow-down toast so a 429 storm doesn't spam notifications. */
    private lastRateLimitToastAt = 0;

    /** Throttle the transient-quota toast so a burst of rejected triggers shows one message. */
    private lastTransientQuotaToastAt = 0;

    /**
     * Show the right UX for a `QUOTA_EXCEEDED` body. Formats `current`/`limit` according to the
     * entitlement's unit (bytes → GiB, rps → req/s, etc.).
     *
     * A *transient* entitlement (e.g. `max_concurrent_runs`) renders a calm "wait, then try again"
     * toast — the limit clears on its own as in-flight work finishes, so an upgrade modal would be
     * the wrong next step. Everything else gets the upgrade prompt, de-duped so concurrent 403s
     * don't stack modals.
     */
    showQuotaExceeded(body: QuotaExceededError): void {
        const meta = metaFor(body.entitlement_key);

        if (isTransientEntitlement(body.entitlement_key)) {
            this.showTransientQuota(formatValue(body.limit, meta.unit), meta.label);
            return;
        }

        if (this.openDialog) return;

        const data: QuotaUpgradeDialogData = {
            label: meta.label,
            icon: meta.icon,
            current: formatValue(body.current, meta.unit),
            limit: formatValue(body.limit, meta.unit)
        };

        this.openDialog = this.dialog.open(QuotaUpgradeDialogComponent, {
            width: '480px',
            autoFocus: false,
            data
        });
        this.openDialog.afterClosed().subscribe(() => {
            this.openDialog = null;
        });
    }

    /**
     * Calm "you've hit a momentary ceiling — wait, then retry" toast for a transient entitlement.
     * Names the limit so the user understands *why* (e.g. "your limit of 5 concurrent runs"), and
     * is throttled so a burst of rejected triggers shows one message, not a stack.
     */
    private showTransientQuota(limit: string, label: string): void {
        const now = Date.now();
        if (now - this.lastTransientQuotaToastAt < 5000) return;
        this.lastTransientQuotaToastAt = now;

        const noun = label.toLowerCase();
        this.toast.info(`You've reached your limit of ${limit} ${noun}. Wait for one to finish, then try again.`);
    }

    /**
     * Show the calm slow-down notice for a `RATE_LIMITED` response. `retryAfterSeconds` comes from
     * the `Retry-After` header (or the body); when known we tell the user exactly how long to wait.
     */
    showRateLimited(retryAfterSeconds: number | null): void {
        // One notice per ~5s window — a flood of 429s is one message, not fifty.
        const now = Date.now();
        if (now - this.lastRateLimitToastAt < 5000) return;
        this.lastRateLimitToastAt = now;

        const wait = retryAfterSeconds && retryAfterSeconds > 0
            ? ` Try again in ${retryAfterSeconds}s.`
            : ' Try again in a moment.';
        this.toast.info(`You're going a little fast.${wait}`);
    }
}
